import { intl, UUID } from '@beelday/common'
import { useSelector } from 'app-redux'
import { Config } from 'common/config'
import { getMultipleChoicePollingApi } from 'features/pollings/multiple-choice-polling/multiple-choice-polling-api'
import { useState } from 'react'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import { ConfiguredArchivedActivity } from './configured-archived-activity'
import { countMine, toggleOption } from './model/answers'
import { MultipleChoiceView } from './presentation/poll-views'
import { Confirmation } from './presentation/primitives'
import { useConfirmedSend } from './use-confirmed-send'

/**
 * A multiple-choice poll (single or multiple answer) on the phone.
 *
 * Results are not shown: in the room they are on the projector, and a phone
 * full of bar charts pulls eyes away from the screen the trainer talks about.
 */
export const ConfiguredMultipleChoice = (): JSX.Element | null => {
	const roomAddress = useAssertedJoinedRoomAddress()
	const me = useAssertedWorkflowUser()
	const poll = useSelector(
		state => state.multipleChoicePolling.pollingDefinition
	)
	const mine = useSelector(state =>
		countMine(state.multipleChoicePolling.questionWithAnswers, me.id)
	)
	const { status, send } = useConfirmedSend(mine)
	const [selected, setSelected] = useState<UUID[]>([])

	if (!poll) {
		return null
	}

	if (poll.doneInPreviousSession) {
		return (
			<ConfiguredArchivedActivity
				toolType="POLLING_MULTIPLE_CHOICE"
				title={poll.question}
			/>
		)
	}

	if (mine > 0 && status !== 'sending') {
		return (
			<Confirmation
				title={<intl.Translate>in_person.mobile.sent.title</intl.Translate>}
				description={
					<intl.Translate>in_person.mobile.sent.poll</intl.Translate>
				}
				testId="in_person.mobile.poll.sent"
			/>
		)
	}

	// Only ids of this poll's options may go out: anything else the server
	// drops while still answering 2xx.
	const valid = selected.filter(id =>
		poll.possibleAnswers.some(answer => answer.answerId === id)
	)

	return (
		<MultipleChoiceView
			question={poll.question}
			options={poll.possibleAnswers.map(answer => ({
				id: answer.answerId,
				text: answer.text,
			}))}
			multiple={poll.allowForMultipleAnswer}
			selected={valid}
			sending={status === 'sending'}
			failed={status === 'failed'}
			onToggle={id =>
				setSelected(current =>
					toggleOption(current, id, poll.allowForMultipleAnswer)
				)
			}
			onSend={() =>
				send(() =>
					getMultipleChoicePollingApi(Config.beeldayBackendUrl).sendAnswer(
						roomAddress,
						poll.id,
						valid
					)
				)
			}
		/>
	)
}

export default ConfiguredMultipleChoice
